import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  AcademicCapIcon,
  CurrencyPoundIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  calculateMultiPortfolioGoalValue,
  calculateGoalMonthlyContribution,
  convertCurrency,
} from '../../utils/calculations';
import { formatCurrency } from '../../utils/formatters';
import SchoolFeePlanModal from './SchoolFeePlanModal';

const SCENARIO_LABELS = {
  maxDrawdown: 'Max Savings Draw-Down',
  spreadEvenly: 'Spread Evenly',
  preserveCapital: 'Monthly Only',
};

const SCENARIO_DESCRIPTIONS = {
  maxDrawdown: 'Use savings first to minimise monthly payments',
  spreadEvenly: 'Divide savings equally across all school years',
  preserveCapital: 'Pay entirely from monthly income, preserve savings',
};

const SchoolFeePlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    schoolFeePlans,
    goals,
    portfolios,
    transactions,
    exchangeRates,
    settings,
    updateSchoolFeePlan,
    deleteSchoolFeePlan,
  } = useData();

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingFeeIdx, setEditingFeeIdx] = useState(null);
  const [editingFeeValue, setEditingFeeValue] = useState('');
  const [activeScenario, setActiveScenario] = useState('maxDrawdown');
  const [customMonthly, setCustomMonthly] = useState(0);

  const plan = schoolFeePlans.find(p => p.id === id);
  const linkedGoal = plan?.linkedGoalId ? goals.find(g => g.id === plan.linkedGoalId) : null;

  // Current savings from linked goal
  const currentSavings = useMemo(() => {
    if (!linkedGoal) return 0;
    const goalValue = calculateMultiPortfolioGoalValue(linkedGoal, portfolios, exchangeRates);
    return convertCurrency(goalValue, linkedGoal.currency || 'GBP', plan?.currency || 'GBP', exchangeRates);
  }, [linkedGoal, portfolios, exchangeRates, plan?.currency]);

  // Monthly contribution to linked goal
  const monthlyContribution = useMemo(() => {
    if (!linkedGoal) return 0;
    const monthly = calculateGoalMonthlyContribution(linkedGoal, portfolios, exchangeRates);
    return convertCurrency(monthly, linkedGoal.currency || 'GBP', plan?.currency || 'GBP', exchangeRates);
  }, [linkedGoal, portfolios, exchangeRates, plan?.currency]);

  // Weighted average return rate from linked goal's portfolios
  const annualGrowthRate = useMemo(() => {
    if (!linkedGoal?.allocations || linkedGoal.allocations.length === 0) return 5;
    let totalValue = 0;
    let weightedReturn = 0;
    linkedGoal.allocations.forEach(alloc => {
      const portfolio = portfolios.find(p => p.id === alloc.portfolioId);
      if (!portfolio) return;
      let allocValue;
      if (alloc.type === 'value' || alloc.type === 'amount') {
        allocValue = alloc.value || 0;
      } else {
        const pct = alloc.value !== undefined ? alloc.value : alloc.percentage || 0;
        allocValue = (portfolio.currentValue || 0) * pct / 100;
      }
      const converted = convertCurrency(allocValue, portfolio.currency, plan?.currency || 'GBP', exchangeRates);
      totalValue += converted;
      weightedReturn += converted * (portfolio.averageReturn || portfolio.estimatedReturnRate || 5);
    });
    return totalValue > 0 ? weightedReturn / totalValue : 5;
  }, [linkedGoal, portfolios, exchangeRates, plan?.currency]);

  // Project savings at school start
  const projectedSavingsAtStart = useMemo(() => {
    if (!plan?.feeSchedule?.length) return currentSavings;
    const startDate = new Date(plan.feeSchedule[0].startYear, 8, 1); // Sept of start year
    const now = new Date();
    const monthsToStart = Math.max(0, (startDate.getFullYear() - now.getFullYear()) * 12 + (startDate.getMonth() - now.getMonth()));
    const monthlyRate = annualGrowthRate / 100 / 12;
    // FV = PV * (1+r)^n + PMT * ((1+r)^n - 1) / r
    if (monthlyRate === 0) return currentSavings + monthlyContribution * monthsToStart;
    const growthFactor = Math.pow(1 + monthlyRate, monthsToStart);
    return currentSavings * growthFactor + monthlyContribution * (growthFactor - 1) / monthlyRate;
  }, [plan, currentSavings, monthlyContribution, annualGrowthRate]);

  // Calculate scenarios
  const scenarios = useMemo(() => {
    if (!plan?.feeSchedule?.length) return {};
    const fees = plan.feeSchedule;
    const totalFees = fees.reduce((s, f) => s + f.annualFee, 0);
    const numYears = fees.length;
    const monthsPerYear = 10; // Sept-June typical school year payments

    const buildScenario = (getAnnualDraw) => {
      let balance = projectedSavingsAtStart;
      const rows = [];
      fees.forEach((fee, i) => {
        // Grow savings during summer (2 months) between years
        if (i > 0) {
          balance *= Math.pow(1 + annualGrowthRate / 100 / 12, 2);
        }
        const draw = getAnnualDraw(fee.annualFee, balance, i, numYears, totalFees);
        const fromSavings = Math.min(draw, Math.max(0, balance));
        const remaining = fee.annualFee - fromSavings;
        const monthlyPayment = remaining > 0 ? remaining / monthsPerYear : 0;
        balance -= fromSavings;
        // Savings grow during the school year on remaining balance
        balance *= Math.pow(1 + annualGrowthRate / 100 / 12, monthsPerYear);
        rows.push({
          ...fee,
          fromSavings,
          monthlyPayment,
          savingsBalance: Math.max(0, balance),
        });
      });
      return rows;
    };

    // Scenario 1: Max draw-down — use as much savings as possible
    const maxDrawdown = buildScenario((fee, balance) => Math.min(fee, Math.max(0, balance)));

    // Scenario 2: Spread evenly — equal annual draw from savings
    const evenDraw = projectedSavingsAtStart / numYears;
    const spreadEvenly = buildScenario((fee, balance) => Math.min(evenDraw, fee, Math.max(0, balance)));

    // Scenario 3: Preserve capital — no savings draw-down
    const preserveCapital = buildScenario(() => 0);

    return { maxDrawdown, spreadEvenly, preserveCapital };
  }, [plan, projectedSavingsAtStart, annualGrowthRate]);

  // Custom scenario
  const customScenario = useMemo(() => {
    if (!plan?.feeSchedule?.length || customMonthly <= 0) return null;
    const fees = plan.feeSchedule;
    const monthsPerYear = 10;
    let balance = projectedSavingsAtStart;
    const rows = [];
    fees.forEach((fee, i) => {
      if (i > 0) {
        balance *= Math.pow(1 + annualGrowthRate / 100 / 12, 2);
      }
      const fromMonthly = customMonthly * monthsPerYear;
      const shortfall = Math.max(0, fee.annualFee - fromMonthly);
      const fromSavings = Math.min(shortfall, Math.max(0, balance));
      const actualMonthly = (fee.annualFee - fromSavings) / monthsPerYear;
      balance -= fromSavings;
      balance *= Math.pow(1 + annualGrowthRate / 100 / 12, monthsPerYear);
      rows.push({
        ...fee,
        fromSavings,
        monthlyPayment: actualMonthly,
        savingsBalance: Math.max(0, balance),
      });
    });
    return rows;
  }, [plan, projectedSavingsAtStart, annualGrowthRate, customMonthly]);

  if (!plan) {
    return (
      <div className="max-w-4xl mx-auto py-8 text-center">
        <p className="text-gray-500">School fee plan not found.</p>
        <button onClick={() => navigate('/school-fees')} className="text-blue-600 text-sm mt-2 hover:underline">
          Back to School Fees
        </button>
      </div>
    );
  }

  const totalFees = plan.feeSchedule.reduce((s, f) => s + f.annualFee, 0);
  const currency = plan.currency || 'GBP';

  const handleInlineFeeEdit = (idx) => {
    setEditingFeeIdx(idx);
    setEditingFeeValue(plan.feeSchedule[idx].annualFee.toString());
  };

  const handleInlineFeeSave = (idx) => {
    const newValue = parseFloat(editingFeeValue) || 0;
    const updatedSchedule = plan.feeSchedule.map((item, i) =>
      i === idx ? { ...item, annualFee: newValue } : item
    );
    updateSchoolFeePlan(plan.id, { feeSchedule: updatedSchedule });
    setEditingFeeIdx(null);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete fee plan for ${plan.childName}?`)) {
      deleteSchoolFeePlan(plan.id);
      navigate('/school-fees');
    }
  };

  const activeRows = activeScenario === 'custom' ? customScenario : scenarios[activeScenario];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/school-fees')} className="text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <AcademicCapIcon className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">{plan.childName}</h1>
            </div>
            {linkedGoal && (
              <p className="text-sm text-gray-500 mt-0.5">
                Linked to: <span className="font-medium text-gray-700">{linkedGoal.name}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditModalVisible(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <PencilIcon className="w-4 h-4" /> Edit
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
          >
            <TrashIcon className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Fees</p>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(totalFees, currency, 0)}</p>
          <p className="text-[10px] text-gray-400">{plan.feeSchedule.length} years</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Current Savings</p>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(currentSavings, currency, 0)}</p>
          {linkedGoal && <p className="text-[10px] text-gray-400">{linkedGoal.name}</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Projected at Start</p>
          <p className="text-lg font-bold text-green-700">{formatCurrency(projectedSavingsAtStart, currency, 0)}</p>
          <p className="text-[10px] text-gray-400">{formatCurrency(monthlyContribution, currency, 0)}/mo + {annualGrowthRate.toFixed(1)}% growth</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Shortfall</p>
          <p className={`text-lg font-bold ${totalFees > projectedSavingsAtStart ? 'text-red-600' : 'text-green-700'}`}>
            {totalFees > projectedSavingsAtStart
              ? formatCurrency(totalFees - projectedSavingsAtStart, currency, 0)
              : 'Fully funded'}
          </p>
          {totalFees > projectedSavingsAtStart && (
            <p className="text-[10px] text-gray-400">
              {formatCurrency((totalFees - projectedSavingsAtStart) / (plan.feeSchedule.length * 10), currency, 0)}/mo avg needed
            </p>
          )}
        </div>
      </div>

      {/* Fee Schedule Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Fee Schedule</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Year Group</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Academic Year</th>
                <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Annual Fee</th>
                <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Monthly (÷10)</th>
              </tr>
            </thead>
            <tbody>
              {plan.feeSchedule.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-1.5 px-2 text-gray-700">{item.academicYear}</td>
                  <td className="py-1.5 px-2 text-gray-500">Sept {item.startYear} – Jul {item.endYear}</td>
                  <td className="py-1.5 px-2 text-right">
                    {editingFeeIdx === idx ? (
                      <input
                        type="number"
                        value={editingFeeValue}
                        onChange={(e) => setEditingFeeValue(e.target.value)}
                        onBlur={() => handleInlineFeeSave(idx)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInlineFeeSave(idx)}
                        className="w-24 px-2 py-0.5 text-sm border border-blue-300 rounded text-right focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="cursor-pointer hover:text-blue-600 font-medium text-gray-900"
                        onClick={() => handleInlineFeeEdit(idx)}
                        title="Click to edit"
                      >
                        {formatCurrency(item.annualFee, currency, 0)}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right text-gray-500">
                    {formatCurrency(item.annualFee / 10, currency, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold">
                <td className="py-2 px-2 text-gray-900" colSpan={2}>Total</td>
                <td className="py-2 px-2 text-right text-gray-900">{formatCurrency(totalFees, currency, 0)}</td>
                <td className="py-2 px-2 text-right text-gray-500">{formatCurrency(totalFees / plan.feeSchedule.length / 10, currency, 0)} avg</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Draw-Down Scenarios */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Payment Scenarios</h2>
        <p className="text-xs text-gray-500 mb-4">
          Based on {formatCurrency(projectedSavingsAtStart, currency, 0)} projected savings at school start,
          with {annualGrowthRate.toFixed(1)}% annual growth on remaining balance
        </p>

        {/* Scenario Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(SCENARIO_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveScenario(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                activeScenario === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setActiveScenario('custom')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              activeScenario === 'custom'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Custom
          </button>
        </div>

        {/* Scenario Description */}
        {activeScenario !== 'custom' && (
          <p className="text-xs text-gray-500 mb-3 italic">{SCENARIO_DESCRIPTIONS[activeScenario]}</p>
        )}

        {/* Custom Monthly Input */}
        {activeScenario === 'custom' && (
          <div className="flex items-center gap-2 mb-4">
            <label className="text-xs text-gray-600">Monthly payment you can afford:</label>
            <div className="flex items-center gap-1">
              <CurrencyPoundIcon className="w-4 h-4 text-gray-400" />
              <input
                type="number"
                value={customMonthly}
                onChange={(e) => setCustomMonthly(parseFloat(e.target.value) || 0)}
                className="w-28 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500"
                min={0}
                step={100}
                placeholder="e.g. 1500"
              />
              <span className="text-xs text-gray-400">/mo</span>
            </div>
          </div>
        )}

        {/* Scenario Table */}
        {activeRows && activeRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Year</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Annual Fee</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">From Savings</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 bg-blue-50">Monthly Payment</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Savings Balance</th>
                </tr>
              </thead>
              <tbody>
                {activeRows.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="py-1.5 px-2 text-gray-700">
                      {row.academicYear}
                      <span className="text-[10px] text-gray-400 ml-1">{row.startYear}–{row.endYear}</span>
                    </td>
                    <td className="py-1.5 px-2 text-right text-gray-900">{formatCurrency(row.annualFee, currency, 0)}</td>
                    <td className="py-1.5 px-2 text-right text-gray-700">
                      {row.fromSavings > 0 ? formatCurrency(row.fromSavings, currency, 0) : '–'}
                    </td>
                    <td className="py-1.5 px-2 text-right font-bold text-blue-700 bg-blue-50">
                      {row.monthlyPayment > 0 ? formatCurrency(row.monthlyPayment, currency, 0) : '–'}
                    </td>
                    <td className="py-1.5 px-2 text-right text-gray-500">
                      {formatCurrency(row.savingsBalance, currency, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 font-semibold">
                  <td className="py-2 px-2 text-gray-900">Total</td>
                  <td className="py-2 px-2 text-right text-gray-900">
                    {formatCurrency(activeRows.reduce((s, r) => s + r.annualFee, 0), currency, 0)}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-700">
                    {formatCurrency(activeRows.reduce((s, r) => s + r.fromSavings, 0), currency, 0)}
                  </td>
                  <td className="py-2 px-2 text-right text-blue-700 bg-blue-50">
                    {formatCurrency(
                      activeRows.reduce((s, r) => s + r.monthlyPayment, 0) / activeRows.length,
                      currency, 0
                    )} avg
                  </td>
                  <td className="py-2 px-2 text-right text-gray-500">
                    {formatCurrency(activeRows[activeRows.length - 1]?.savingsBalance || 0, currency, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : activeScenario === 'custom' && customMonthly <= 0 ? (
          <p className="text-xs text-gray-400 italic">Enter a monthly payment amount above to see the breakdown</p>
        ) : null}

        {/* Quick comparison summary */}
        {scenarios.maxDrawdown && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Quick Comparison</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(SCENARIO_LABELS).map(([key, label]) => {
                const rows = scenarios[key];
                if (!rows) return null;
                const avgMonthly = rows.reduce((s, r) => s + r.monthlyPayment, 0) / rows.length;
                const maxMonthly = Math.max(...rows.map(r => r.monthlyPayment));
                const totalFromSavings = rows.reduce((s, r) => s + r.fromSavings, 0);
                return (
                  <div
                    key={key}
                    onClick={() => setActiveScenario(key)}
                    className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                      activeScenario === key ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-[10px] font-medium text-gray-500">{label}</p>
                    <p className="text-sm font-bold text-gray-900 mt-1">{formatCurrency(avgMonthly, currency, 0)}/mo avg</p>
                    <p className="text-[10px] text-gray-400">Max: {formatCurrency(maxMonthly, currency, 0)}/mo</p>
                    <p className="text-[10px] text-gray-400">Savings used: {formatCurrency(totalFromSavings, currency, 0)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <SchoolFeePlanModal
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        plan={plan}
      />
    </div>
  );
};

export default SchoolFeePlanDetail;
